/**
 * Wäscheklassen-Verwaltung - Liste
 * 
 * Pattern: LinesPage-Style (Liste → Detailseite)
 * 
 * UX-Leitlinie:
 * - Klick auf Zeile → Detailseite
 * - Keine Buttons in der Liste
 * - Kein Code in der Liste (nur Name)
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Layers, Plus, RefreshCw, Search, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface LaundryClass {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

export default function LaundryClassesPage() {
  const navigate = useNavigate();
  const [laundryClasses, setLaundryClasses] = useState<LaundryClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadLaundryClasses = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/laundry-classes?includeInactive=true', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLaundryClasses(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Wäscheklassen:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLaundryClasses();
  }, []);

  // Filter + Sort (nur aktive anzeigen, alphabetisch)
  const filtered = useMemo(() => {
    const active = laundryClasses.filter(lc => lc.isActive !== false);
    const searched = !search ? active : active.filter(lc => {
      const s = search.toLowerCase();
      return lc.name.toLowerCase().includes(s);
    });
    return [...searched].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'de'));
  }, [laundryClasses, search]);

  const activeCount = laundryClasses.filter(lc => lc.isActive).length;

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Layers className="w-6 h-6" />
              Wäscheklassen
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {activeCount} Wäscheklassen
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadLaundryClasses}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <Link
              to="/admin/laundry-classes/new"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neue Wäscheklasse
            </Link>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Liste */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {search ? 'Keine Treffer' : 'Keine Wäscheklassen vorhanden'}
              {!search && (
                <Link
                  to="/admin/laundry-classes/new"
                  className="block mx-auto mt-4 text-blue-600 hover:underline"
                >
                  Erste Wäscheklasse anlegen
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map(lc => (
                <div
                  key={lc.id}
                  className="flex items-center px-4 py-3 hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/admin/laundry-classes/${lc.id}`)}
                >
                  <div className="flex-1">
                    <span className="font-medium">{lc.name}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
